import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoundingBox } from '../types';

/**
 * Props para ProjectBoundsEditor
 */
interface ProjectBoundsEditorProps {
  bounds: BoundingBox;
  hasRectangle: boolean;
  drawInstance: any;
  onDrawRectangle: () => void;
  onClearRectangle: () => void;
  onCopyCoordinates: () => Promise<void>;
}

/**
 * Calcula la distancia aproximada en km entre dos coordenadas usando Haversine
 *
 * @param lat1 - Latitud del primer punto
 * @param lon1 - Longitud del primer punto
 * @param lat2 - Latitud del segundo punto
 * @param lon2 - Longitud del segundo punto
 * @returns Distancia en kilómetros
 */
function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Radio de la Tierra en km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * ProjectBoundsEditor - Editor de área de estudio del proyecto
 *
 * @remarks
 * Incluye:
 * - Botones para dibujar/limpiar rectángulo
 * - Botón para copiar coordenadas
 * - Cálculo y visualización de superficie del área
 *
 * **Cumple con:**
 * - P1: Componente < 200 líneas
 * - P3: Documentación JSDoc completa
 */
export function ProjectBoundsEditor({
  bounds,
  hasRectangle,
  drawInstance,
  onDrawRectangle,
  onClearRectangle,
  onCopyCoordinates,
}: ProjectBoundsEditorProps): JSX.Element {
  const { t } = useTranslation('projects');

  /**
   * Calcula métricas del bounding box
   */
  const metrics = useMemo(() => {
    const { minLng, maxLng, minLat, maxLat } = bounds;

    // Verificar si son bounds continentales por defecto (sin rectángulo)
    const isContinental =
      minLng === -170 && minLat === -60 && maxLng === -30 && maxLat === 75;

    if (isContinental) return null;

    // Ancho (distancia horizontal en la latitud media)
    const midLat = (minLat + maxLat) / 2;
    const width = haversineDistance(midLat, minLng, midLat, maxLng);

    // Alto (distancia vertical)
    const height = haversineDistance(minLat, minLng, maxLat, minLng);

    // Superficie aproximada
    const area = width * height;

    return {
      area: area.toFixed(2),
    };
  }, [bounds]);

  return (
    <div className="form-section">
      <div className="form-group">
        <label>
          {t('form.boundingBox')} <span className="required">{t('form.required')}</span>
        </label>
        <div className="bbox-interactive-info">
          <button
            type="button"
            onClick={onDrawRectangle}
            disabled={!drawInstance || hasRectangle}
            className="btn-draw-rectangle"
          >
            {t('form.defineWorkArea')}
          </button>
          <button
            type="button"
            onClick={onClearRectangle}
            disabled={!drawInstance || !hasRectangle}
            className="btn-clear-rectangle"
          >
            {t('form.clearRectangle')}
          </button>
          <button
            type="button"
            onClick={() => void onCopyCoordinates()}
            disabled={!hasRectangle}
            className="btn-copy-icon"
            title={t('form.copyCoordinatesTooltip')}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </button>
        </div>

        {/* Superficie */}
        {hasRectangle && metrics && (
          <div className="bbox-surface-info">
            <span className="surface-label">{t('form.surfaceAreaLabel')}:</span>
            <span className="surface-value">{metrics.area} km²</span>
          </div>
        )}
      </div>
    </div>
  );
}
